/**
 * FinancialTemplate Component
 *
 * Standard template for displaying all dealership departments with financial data.
 * Displays monthly columns with YTD totals on the right.
 * All departments are shown for consistency - departments not used by a location
 * will display with zero/blank values.
 *
 * Department Structure:
 * - NEW DEPARTMENT (with brand units breakdown)
 * - USED DEPARTMENT
 * - SERVICE DEPARTMENT
 * - PARTS DEPARTMENT
 * - RECON DEPARTMENT
 * - RENTAL DEPARTMENT
 * - BODY SHOP DEPARTMENT
 * - OTHERS DEPARTMENT (other income accounts + Doc Fee)
 * - MISC DEPARTMENT
 * - UNKNOWN DEPARTMENT (catch-all for unmapped accounts)
 */

import React, { useState, useMemo } from 'react';
import {
  HistoricalData,
  AccountData,
  DepartmentSummary,
  Department,
  MonthlyTotals,
  MONTH_NAMES,
  getMonthValue
} from '../types';
import { getLocationUnitConfig, UnitCategory } from '../config/unitCategories';

interface FinancialTemplateProps {
  data: HistoricalData;
  unitCounts?: Record<string, number>;
  formatCurrency: (value: number) => string;
}

// Department display order and configuration
const DEPARTMENT_CONFIG: Array<{
  key: Department;
  title: string;
  hasUnits?: boolean;
  hasFI?: boolean;
  hasTotalGross?: boolean;
  hasVariableExpenses?: boolean;
  hasSemiFixedExpenses?: boolean;
  isIncomeOnly?: boolean;
  isRental?: boolean;
  isMiscClearing?: boolean;
}> = [
  { key: 'NEW_VEHICLE', title: 'NEW DEPARTMENT', hasUnits: true, hasFI: true, hasTotalGross: true, hasVariableExpenses: true, hasSemiFixedExpenses: true },
  { key: 'USED_VEHICLE', title: 'USED DEPARTMENT', hasUnits: true, hasFI: true, hasTotalGross: true, hasVariableExpenses: true, hasSemiFixedExpenses: true },
  { key: 'SERVICE', title: 'SERVICE DEPARTMENT' },
  { key: 'PARTS', title: 'PARTS DEPARTMENT' },
  { key: 'RECON', title: 'RECON DEPARTMENT' },
  { key: 'RENTAL', title: 'RENTAL DEPARTMENT', isRental: true },
  { key: 'BODY_SHOP', title: 'BODY SHOP DEPARTMENT' },
  { key: 'OTHER', title: 'OTHERS DEPARTMENT', isIncomeOnly: true },
  { key: 'MISC', title: 'MISC CLEARING ACCOUNTS', isMiscClearing: true },
  { key: 'UNKNOWN', title: 'UNKNOWN DEPARTMENT' },
];

// Color schemes for different row types
const ROW_COLORS = {
  sales: 'bg-blue-50 text-blue-900',
  gross: 'bg-green-50 text-green-900',
  fiIncome: 'bg-green-50 text-green-900',
  totalGross: 'bg-green-100 text-green-900',
  variableExpense: 'bg-orange-50 text-orange-900',
  semiFixedExpense: 'bg-amber-50 text-amber-900',
  fixedExpense: 'bg-purple-50 text-purple-900',
  sellingExpense: 'bg-orange-50 text-orange-900',
  otherExpense: 'bg-gray-100 text-gray-900',
  totalExpense: 'bg-red-50 text-red-900',
  profit: 'bg-emerald-100 text-emerald-900',
  loss: 'bg-red-100 text-red-900',
  otherIncome: 'bg-teal-50 text-teal-900',
  rentalIncome: 'bg-teal-50 text-teal-900',
  miscClearing: 'bg-gray-50 text-gray-900',
  totalMisc: 'bg-gray-200 text-gray-900',
};

export const FinancialTemplate: React.FC<FinancialTemplateProps> = ({
  data,
  unitCounts = {},
  formatCurrency,
}) => {
  // Track which departments are expanded
  const [expandedDepartments, setExpandedDepartments] = useState<Set<string>>(new Set());
  // Track which expense categories are expanded within departments
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Toggle department expansion
  const toggleDepartment = (deptKey: string) => {
    setExpandedDepartments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(deptKey)) {
        newSet.delete(deptKey);
      } else {
        newSet.add(deptKey);
      }
      return newSet;
    });
  };

  // Toggle category expansion within a department
  const toggleCategory = (categoryKey: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryKey)) {
        newSet.delete(categoryKey);
      } else {
        newSet.add(categoryKey);
      }
      return newSet;
    });
  };

  // Get unit configuration for the location
  const unitConfig = useMemo(() => {
    return getLocationUnitConfig(data.locationId);
  }, [data.locationId]);

  // Group accounts by department and expense category (using new DOC-based categories)
  const accountsByDeptCategory = useMemo(() => {
    const grouped = new Map<string, AccountData[]>();

    data.accounts?.forEach(account => {
      // Use expenseCategory if available, fall back to category for backward compatibility
      const category = account.expenseCategory || account.category || 'UNKNOWN';
      const key = `${account.department || 'UNKNOWN'}-${category}`;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(account);
    });

    return grouped;
  }, [data.accounts]);

  // Get accounts for a specific department and expense category
  const getAccounts = (dept: Department, category: string): AccountData[] => {
    const key = `${dept}-${category}`;
    return accountsByDeptCategory.get(key) || [];
  };

  // Get department summary
  const getDeptSummary = (dept: Department): DepartmentSummary | undefined => {
    return data.departmentSummaries?.find(s => s.department === dept);
  };

  // Calculate total units for new or used
  const getTotalUnits = (type: 'NEW_VEHICLE' | 'USED_VEHICLE'): number => {
    if (!unitConfig) return 0;
    const categories = type === 'NEW_VEHICLE'
      ? unitConfig.newVehicleCategories
      : unitConfig.usedVehicleCategories;
    return categories.reduce((sum, cat) => sum + (unitCounts[cat.key] || 0), 0);
  };

  // Render unit counts section for vehicle departments with monthly breakdown
  const renderUnitCounts = (type: 'NEW_VEHICLE' | 'USED_VEHICLE') => {
    if (!unitConfig) return null;

    const categories = type === 'NEW_VEHICLE'
      ? unitConfig.newVehicleCategories
      : unitConfig.usedVehicleCategories;

    if (categories.length === 0) return null;

    const totalLabel = type === 'NEW_VEHICLE' ? 'New Vehicle Units (Total)' : 'Used Vehicle Units (Total)';
    const totalUnits = getTotalUnits(type);
    const apiTotal = type === 'NEW_VEHICLE'
      ? data.unitCounts?.totalNewUnits
      : data.unitCounts?.totalUsedUnits;

    // Get monthly data
    const monthlyTotals = type === 'NEW_VEHICLE'
      ? data.unitCounts?.monthlyNewUnits
      : data.unitCounts?.monthlyUsedUnits;

    const monthlyByBrand = type === 'NEW_VEHICLE'
      ? data.unitCounts?.monthlyNewUnitsByBrand
      : data.unitCounts?.monthlyUsedUnitsByBrand;

    // Format number for display (no decimals for units)
    const formatUnits = (value: number) => value.toLocaleString();

    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          {/* Total Units Row */}
          <tbody>
            <tr className="bg-indigo-100 border-b border-indigo-200">
              <td className="min-w-[180px] lg:min-w-0 lg:w-[18%] px-2 lg:px-3 py-2 text-xs lg:text-sm font-semibold text-indigo-900 sticky lg:static left-0 bg-indigo-100">
                <div className="flex items-center">
                  <span className="mr-2 w-4 flex-shrink-0"></span>
                  <span>{totalLabel}</span>
                </div>
              </td>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                <td
                  key={month}
                  className="min-w-[70px] lg:min-w-0 lg:w-[6%] px-1 py-2 text-right text-xs lg:text-sm font-semibold text-indigo-900"
                >
                  {formatUnits(getMonthValue(monthlyTotals, month))}
                </td>
              ))}
              <td className="min-w-[85px] lg:min-w-0 lg:w-[10%] px-2 lg:px-3 py-2 text-right text-xs lg:text-sm font-bold text-indigo-900 bg-indigo-200">
                {formatUnits(apiTotal !== undefined ? apiTotal : totalUnits)}
              </td>
            </tr>

            {/* Brand Sub-rows */}
            {categories.map((cat: UnitCategory) => {
              const brandMonthly = monthlyByBrand?.[cat.key];
              const brandYtd = unitCounts[cat.key] || 0;

              return (
                <tr key={cat.key} className="bg-indigo-50 border-b border-indigo-100 hover:bg-indigo-100">
                  <td className="min-w-[180px] lg:min-w-0 lg:w-[18%] px-2 lg:px-3 py-1.5 text-xs sticky lg:static left-0 bg-indigo-50" style={{ paddingLeft: '32px' }}>
                    <div className="flex items-center text-indigo-800">
                      <span className="mr-2">•</span>
                      <span className="font-medium">{cat.label}</span>
                    </div>
                  </td>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                    <td
                      key={month}
                      className="min-w-[70px] lg:min-w-0 lg:w-[6%] px-1 py-1.5 text-right text-xs text-indigo-700"
                    >
                      {formatUnits(getMonthValue(brandMonthly, month))}
                    </td>
                  ))}
                  <td className="min-w-[85px] lg:min-w-0 lg:w-[10%] px-2 lg:px-3 py-1.5 text-right text-xs font-medium text-indigo-800 bg-indigo-100">
                    {formatUnits(brandYtd)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Render a single data row with monthly values
  const renderDataRow = (
    label: string,
    ytdValue: number,
    monthlyData: MonthlyTotals | undefined,
    colorClass: string,
    isBold: boolean = false,
    isExpandable: boolean = false,
    expandKey?: string,
    accounts?: AccountData[]
  ) => {
    const isExpanded = expandKey ? expandedCategories.has(expandKey) : false;
    const hasAccounts = accounts && accounts.length > 0;

    return (
      <React.Fragment key={label}>
        <tr
          className={`${colorClass} border-b border-gray-200 ${isExpandable && hasAccounts ? 'cursor-pointer hover:opacity-90' : ''}`}
          onClick={() => isExpandable && hasAccounts && expandKey && toggleCategory(expandKey)}
        >
          {/* Label Column - Sticky on mobile only */}
          <td className={`min-w-[180px] lg:min-w-0 lg:w-[18%] px-2 lg:px-3 py-2 text-xs lg:text-sm ${isBold ? 'font-semibold' : ''} sticky lg:static left-0 ${colorClass}`}>
            <div className="flex items-center">
              {isExpandable && hasAccounts && (
                <span className="mr-2 text-gray-500 w-4 flex-shrink-0">
                  {isExpanded ? '▼' : '▶'}
                </span>
              )}
              {(!isExpandable || !hasAccounts) && <span className="mr-2 w-4 flex-shrink-0"></span>}
              <span className="truncate">{label}</span>
            </div>
          </td>
          {/* Monthly Columns */}
          {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
            <td
              key={month}
              className={`min-w-[70px] lg:min-w-0 lg:w-[6%] px-1 py-2 text-right text-xs lg:text-sm ${isBold ? 'font-semibold' : ''}`}
            >
              {formatCurrency(getMonthValue(monthlyData, month))}
            </td>
          ))}
          {/* YTD Column */}
          <td className={`min-w-[85px] lg:min-w-0 lg:w-[10%] px-2 lg:px-3 py-2 text-right text-xs lg:text-sm font-semibold bg-gray-100`}>
            {formatCurrency(ytdValue)}
          </td>
        </tr>

        {/* Expanded Account Rows */}
        {isExpanded && accounts?.map((acc, idx) => (
          <tr key={`${label}-${acc.account}-${idx}`} className="bg-gray-50 border-b border-gray-100 hover:bg-gray-100">
            <td className="min-w-[180px] lg:min-w-0 lg:w-[18%] px-2 lg:px-3 py-1.5 text-xs sticky lg:static left-0 bg-gray-50" style={{ paddingLeft: '32px' }}>
              <div className="flex flex-col lg:flex-row lg:items-center">
                <span className="font-mono text-gray-600 mr-2 flex-shrink-0">{acc.account}</span>
                <span className="text-gray-500 truncate text-xs">{acc.description || ''}</span>
              </div>
            </td>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
              <td key={month} className="min-w-[70px] lg:min-w-0 lg:w-[6%] px-1 py-1.5 text-right text-xs text-gray-700">
                {formatCurrency(getMonthValue(acc.monthlyTotals, month))}
              </td>
            ))}
            <td className="min-w-[85px] lg:min-w-0 lg:w-[10%] px-2 lg:px-3 py-1.5 text-right text-xs font-medium bg-gray-100">
              {formatCurrency(acc.ytdTotal)}
            </td>
          </tr>
        ))}
      </React.Fragment>
    );
  };

  // Render department content
  const renderDepartmentContent = (config: typeof DEPARTMENT_CONFIG[0]) => {
    const summary = getDeptSummary(config.key);
    if (!summary) return null;

    const monthlyData = summary.monthlyData;
    const deptLabel = config.title.replace(' DEPARTMENT', '').replace(' CLEARING ACCOUNTS', '');

    // ========================================
    // OTHERS DEPARTMENT - Income only
    // ========================================
    if (config.isIncomeOnly) {
      const incomeAccounts = getAccounts(config.key, 'OTHER_INCOME');
      return (
        <>
          {renderDataRow(
            'Others Income Total',
            summary.otherIncome,
            monthlyData?.otherIncome,
            ROW_COLORS.otherIncome,
            true,
            true,
            `${config.key}-OTHER_INCOME`,
            incomeAccounts
          )}
          {renderDataRow(
            `Operating Profit - ${deptLabel}`,
            summary.operatingProfit,
            monthlyData?.operatingProfit,
            summary.operatingProfit >= 0 ? ROW_COLORS.profit : ROW_COLORS.loss,
            true
          )}
        </>
      );
    }

    // ========================================
    // MISC CLEARING ACCOUNTS - Special layout
    // ========================================
    if (config.isMiscClearing) {
      const clearingAccounts = getAccounts(config.key, 'CLEARING');
      const allMiscAccounts = [...clearingAccounts, ...getAccounts(config.key, 'UNKNOWN')];
      return (
        <>
          {renderDataRow(
            'Clearing Accounts',
            summary.totalExpenses,
            monthlyData?.totalExpenses,
            ROW_COLORS.miscClearing,
            false,
            true,
            `${config.key}-CLEARING`,
            allMiscAccounts
          )}
          {renderDataRow(
            'Total Misc',
            summary.operatingProfit,
            monthlyData?.operatingProfit,
            ROW_COLORS.totalMisc,
            true
          )}
        </>
      );
    }

    // ========================================
    // RENTAL DEPARTMENT - Rental Income + Expenses
    // ========================================
    if (config.isRental) {
      return (
        <>
          {/* Rental Income */}
          {renderDataRow(
            'Rental Income YTD',
            summary.sales,
            monthlyData?.sales,
            ROW_COLORS.rentalIncome,
            true
          )}

          {/* Selling Expense */}
          {(summary.salesExpenses || 0) !== 0 && renderDataRow(
            `Selling Expense - ${deptLabel}`,
            summary.salesExpenses || 0,
            monthlyData?.salesExpenses,
            ROW_COLORS.sellingExpense,
            false,
            true,
            `${config.key}-SALES`,
            getAccounts(config.key, 'SALES')
          )}

          {/* Fixed Expense */}
          {(summary.fixedExpenses || 0) !== 0 && renderDataRow(
            `Fixed Expense - ${deptLabel}`,
            summary.fixedExpenses || 0,
            monthlyData?.fixedExpenses,
            ROW_COLORS.fixedExpense,
            false,
            true,
            `${config.key}-FIXED`,
            getAccounts(config.key, 'FIXED')
          )}

          {/* Total Expenses */}
          {renderDataRow(
            `Total Expenses - ${deptLabel}`,
            summary.totalExpenses,
            monthlyData?.totalExpenses,
            ROW_COLORS.totalExpense,
            true
          )}

          {/* Operating Profit */}
          {renderDataRow(
            `Operating Profit - ${deptLabel}`,
            summary.operatingProfit,
            monthlyData?.operatingProfit,
            summary.operatingProfit >= 0 ? ROW_COLORS.profit : ROW_COLORS.loss,
            true
          )}
        </>
      );
    }

    // ========================================
    // NEW & USED DEPARTMENTS - Full layout with F&I and Total Gross
    // ========================================
    if (config.hasFI && config.hasTotalGross) {
      return (
        <>
          {/* Sales YTD */}
          {renderDataRow(
            'Sales YTD',
            summary.sales,
            monthlyData?.sales,
            ROW_COLORS.sales,
            true
          )}

          {/* Gross (Frontend) YTD */}
          {renderDataRow(
            'Gross (Frontend) YTD',
            summary.frontGross,
            monthlyData?.frontGross,
            ROW_COLORS.gross,
            true
          )}

          {/* F&I (Backend) YTD */}
          {renderDataRow(
            'F&I (Backend) YTD',
            summary.fiIncome,
            monthlyData?.fiIncome,
            ROW_COLORS.fiIncome,
            true
          )}

          {/* Total Gross */}
          {renderDataRow(
            'Total Gross',
            summary.totalGross,
            monthlyData?.totalGross,
            ROW_COLORS.totalGross,
            true
          )}

          {/* Variable Expenses */}
          {(summary.variableExpenses || 0) !== 0 && renderDataRow(
            `Variable Expenses - ${deptLabel}`,
            summary.variableExpenses || 0,
            monthlyData?.variableExpenses,
            ROW_COLORS.variableExpense,
            false,
            true,
            `${config.key}-VARIABLE`,
            getAccounts(config.key, 'VARIABLE')
          )}

          {/* Semi-Fixed Expenses */}
          {(summary.semiFixedExpenses || 0) !== 0 && renderDataRow(
            `Semi-Fixed Expenses - ${deptLabel}`,
            summary.semiFixedExpenses || 0,
            monthlyData?.semiFixedExpenses,
            ROW_COLORS.semiFixedExpense,
            false,
            true,
            `${config.key}-SEMI_FIXED`,
            getAccounts(config.key, 'SEMI_FIXED')
          )}

          {/* Fixed Expenses */}
          {(summary.fixedExpenses || 0) !== 0 && renderDataRow(
            `Fixed Expenses - ${deptLabel}`,
            summary.fixedExpenses || 0,
            monthlyData?.fixedExpenses,
            ROW_COLORS.fixedExpense,
            false,
            true,
            `${config.key}-FIXED`,
            getAccounts(config.key, 'FIXED')
          )}

          {/* Others (Owner Compensation) */}
          {(summary.otherExpenses || 0) !== 0 && renderDataRow(
            `Others - ${deptLabel}`,
            summary.otherExpenses || 0,
            monthlyData?.otherExpenses,
            ROW_COLORS.otherExpense,
            false,
            true,
            `${config.key}-OTHERS`,
            getAccounts(config.key, 'OTHERS')
          )}

          {/* Total Expenses */}
          {renderDataRow(
            `Total Expenses - ${deptLabel}`,
            summary.totalExpenses,
            monthlyData?.totalExpenses,
            ROW_COLORS.totalExpense,
            true
          )}

          {/* Operating Profit */}
          {renderDataRow(
            `Operating Profit - ${deptLabel}`,
            summary.operatingProfit,
            monthlyData?.operatingProfit,
            summary.operatingProfit >= 0 ? ROW_COLORS.profit : ROW_COLORS.loss,
            true
          )}
        </>
      );
    }

    // ========================================
    // SERVICE, PARTS, BODY SHOP, RECON DEPARTMENTS
    // Sales, Gross (sales - COS), Sales Expenses, Fixed, Others
    // ========================================
    return (
      <>
        {/* Sales YTD */}
        {renderDataRow(
          'Sales YTD',
          summary.sales,
          monthlyData?.sales,
          ROW_COLORS.sales,
          true
        )}

        {/* Gross YTD (sales - COS) */}
        {renderDataRow(
          'Gross YTD',
          summary.frontGross,
          monthlyData?.frontGross,
          ROW_COLORS.gross,
          true
        )}

        {/* Sales Expenses */}
        {(summary.salesExpenses || 0) !== 0 && renderDataRow(
          `Sales Expenses - ${deptLabel}`,
          summary.salesExpenses || 0,
          monthlyData?.salesExpenses,
          ROW_COLORS.variableExpense,
          false,
          true,
          `${config.key}-SALES`,
          getAccounts(config.key, 'SALES')
        )}

        {/* Fixed Expenses */}
        {(summary.fixedExpenses || 0) !== 0 && renderDataRow(
          `Fixed Expenses - ${deptLabel}`,
          summary.fixedExpenses || 0,
          monthlyData?.fixedExpenses,
          ROW_COLORS.fixedExpense,
          false,
          true,
          `${config.key}-FIXED`,
          getAccounts(config.key, 'FIXED')
        )}

        {/* Others */}
        {(summary.otherExpenses || 0) !== 0 && renderDataRow(
          `Others - ${deptLabel}`,
          summary.otherExpenses || 0,
          monthlyData?.otherExpenses,
          ROW_COLORS.otherExpense,
          false,
          true,
          `${config.key}-OTHERS`,
          getAccounts(config.key, 'OTHERS')
        )}

        {/* Total Expenses */}
        {renderDataRow(
          `Total Expenses - ${deptLabel}`,
          summary.totalExpenses,
          monthlyData?.totalExpenses,
          ROW_COLORS.totalExpense,
          true
        )}

        {/* Operating Profit */}
        {renderDataRow(
          `Operating Profit - ${deptLabel}`,
          summary.operatingProfit,
          monthlyData?.operatingProfit,
          summary.operatingProfit >= 0 ? ROW_COLORS.profit : ROW_COLORS.loss,
          true
        )}
      </>
    );
  };

  // Render the Location Total Summary table
  const renderLocationTotalSummary = () => {
    return (
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="px-3 md:px-6 py-3 md:py-4 border-b border-gray-200 bg-gray-800">
          <h2 className="text-lg md:text-xl font-bold text-white">
            {data.locationName} - {data.reportType}
          </h2>
          <p className="text-sm text-gray-300 mt-1">
            {data.locationSummary?.departmentCount || 0} departments • {data.locationSummary?.accountCount || 0} accounts
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-600 text-white text-xs lg:text-sm">
                <th className="min-w-[180px] lg:min-w-0 lg:w-[18%] px-2 lg:px-3 py-2 text-left font-semibold sticky lg:static left-0 bg-gray-600">
                  Metric
                </th>
                {MONTH_NAMES.map(month => (
                  <th key={month} className="min-w-[70px] lg:min-w-0 lg:w-[6%] px-1 py-2 text-right font-semibold">
                    {month}
                  </th>
                ))}
                <th className="min-w-[85px] lg:min-w-0 lg:w-[10%] px-2 lg:px-3 py-2 text-right font-bold bg-gray-700">
                  YTD
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Total Sales */}
              <tr className="bg-blue-50 border-b border-gray-200">
                <td className="min-w-[180px] lg:min-w-0 lg:w-[18%] px-2 lg:px-3 py-2 text-xs lg:text-sm font-semibold text-blue-900 sticky lg:static left-0 bg-blue-50">
                  Total Sales
                </td>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                  <td key={month} className="min-w-[70px] lg:min-w-0 lg:w-[6%] px-1 py-2 text-right text-xs lg:text-sm text-blue-900">
                    {formatCurrency(getMonthValue(data.locationSummary?.monthlyData?.totalSales, month))}
                  </td>
                ))}
                <td className="min-w-[85px] lg:min-w-0 lg:w-[10%] px-2 lg:px-3 py-2 text-right text-xs lg:text-sm font-bold text-blue-900 bg-blue-100">
                  {formatCurrency(data.locationSummary?.totalSales || 0)}
                </td>
              </tr>

              {/* Total Gross */}
              <tr className="bg-green-50 border-b border-gray-200">
                <td className="min-w-[180px] lg:min-w-0 lg:w-[18%] px-2 lg:px-3 py-2 text-xs lg:text-sm font-semibold text-green-900 sticky lg:static left-0 bg-green-50">
                  Total Gross
                </td>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                  <td key={month} className="min-w-[70px] lg:min-w-0 lg:w-[6%] px-1 py-2 text-right text-xs lg:text-sm text-green-900">
                    {formatCurrency(getMonthValue(data.locationSummary?.monthlyData?.totalGross, month))}
                  </td>
                ))}
                <td className="min-w-[85px] lg:min-w-0 lg:w-[10%] px-2 lg:px-3 py-2 text-right text-xs lg:text-sm font-bold text-green-900 bg-green-100">
                  {formatCurrency(data.locationSummary?.totalGross || 0)}
                </td>
              </tr>

              {/* Total Expenses */}
              <tr className="bg-orange-50 border-b border-gray-200">
                <td className="min-w-[180px] lg:min-w-0 lg:w-[18%] px-2 lg:px-3 py-2 text-xs lg:text-sm font-semibold text-orange-900 sticky lg:static left-0 bg-orange-50">
                  Total Expenses
                </td>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                  <td key={month} className="min-w-[70px] lg:min-w-0 lg:w-[6%] px-1 py-2 text-right text-xs lg:text-sm text-orange-900">
                    {formatCurrency(getMonthValue(data.locationSummary?.monthlyData?.totalExpenses, month))}
                  </td>
                ))}
                <td className="min-w-[85px] lg:min-w-0 lg:w-[10%] px-2 lg:px-3 py-2 text-right text-xs lg:text-sm font-bold text-orange-900 bg-orange-100">
                  {formatCurrency(data.locationSummary?.totalExpenses || 0)}
                </td>
              </tr>

              {/* Operating Profit */}
              <tr className={(data.locationSummary?.operatingProfit || 0) >= 0 ? 'bg-emerald-100' : 'bg-red-100'}>
                <td className={`min-w-[180px] lg:min-w-0 lg:w-[18%] px-2 lg:px-3 py-3 text-xs lg:text-sm font-bold sticky lg:static left-0 ${(data.locationSummary?.operatingProfit || 0) >= 0 ? 'text-emerald-900 bg-emerald-100' : 'text-red-900 bg-red-100'}`}>
                  Operating Profit
                </td>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                  <td key={month} className={`min-w-[70px] lg:min-w-0 lg:w-[6%] px-1 py-3 text-right text-xs lg:text-sm font-semibold ${(data.locationSummary?.operatingProfit || 0) >= 0 ? 'text-emerald-900' : 'text-red-900'}`}>
                    {formatCurrency(getMonthValue(data.locationSummary?.monthlyData?.operatingProfit, month))}
                  </td>
                ))}
                <td className={`min-w-[85px] lg:min-w-0 lg:w-[10%] px-2 lg:px-3 py-3 text-right text-sm lg:text-base font-bold ${(data.locationSummary?.operatingProfit || 0) >= 0 ? 'text-emerald-900 bg-emerald-200' : 'text-red-900 bg-red-200'}`}>
                  {formatCurrency(data.locationSummary?.operatingProfit || 0)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Location Total Summary - At the top */}
      {renderLocationTotalSummary()}

      {/* Department Sections */}
      {DEPARTMENT_CONFIG.map(config => {
        const summary = getDeptSummary(config.key);
        const isExpanded = expandedDepartments.has(config.key);
        const profitColor = (summary?.operatingProfit || 0) >= 0 ? 'text-green-600' : 'text-red-600';

        return (
          <div key={config.key} className="bg-white rounded-lg shadow">
            {/* Department Header */}
            <div
              className="px-3 md:px-6 py-3 md:py-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50"
              onClick={() => toggleDepartment(config.key)}
            >
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2 md:space-x-3">
                  <span className="text-base md:text-lg text-gray-400">
                    {isExpanded ? '▼' : '▶'}
                  </span>
                  <h3 className="text-base md:text-lg font-semibold text-gray-900">{config.title}</h3>
                </div>
                <div className={`text-base md:text-lg font-bold ${profitColor}`}>
                  {formatCurrency(summary?.operatingProfit || 0)}
                </div>
              </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
              <div>
                {/* Unit Counts (for vehicle departments) */}
                {config.hasUnits && renderUnitCounts(config.key as 'NEW_VEHICLE' | 'USED_VEHICLE')}

                {/* Monthly Data Table */}
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    {/* Column Headers */}
                    <thead className="sticky top-0 z-10">
                      <tr className="bg-gray-700 text-white text-xs lg:text-sm">
                        <th className="min-w-[180px] lg:min-w-0 lg:w-[18%] px-2 lg:px-3 py-2 text-left font-semibold sticky lg:static left-0 bg-gray-700 z-20">
                          Account / Description
                        </th>
                        {MONTH_NAMES.map(month => (
                          <th key={month} className="min-w-[70px] lg:min-w-0 lg:w-[6%] px-1 py-2 text-right font-semibold">
                            {month}
                          </th>
                        ))}
                        <th className="min-w-[85px] lg:min-w-0 lg:w-[10%] px-2 lg:px-3 py-2 text-right font-bold bg-gray-800">
                          YTD
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {renderDepartmentContent(config)}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
